import { useSelector } from "react-redux";
import { useTheme } from "../../common/hooks/useTheme";
import { selectIsOnline } from "../../store/slices/networkSlice";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { LoginStage, LegalityScreens } from "../../common/types/enums";
import { getBotUrl } from "../../config/runtimeConfig";

import styles from "./TelegramLoginScreen.module.scss";
import Button from "../../common/components/button/Button";
import Loader from "../../common/components/loader/Loader";
import BackButton from "../../common/components/backButton/BackButton";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import { useTelegramLogin } from "./useTelegramLogin";
import { useLegalityModal } from "../../common/components/legalityModal/useLegalityModal";
import { FaTelegramPlane } from "react-icons/fa";
import { Navigate } from "react-router-dom";
import { ROUTES } from "../../common/utils/constants";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";

export default function TelegramLoginScreen() {
  const { theme } = useTheme();
  const isOnline = useSelector(selectIsOnline);
  const currentCluster = useSelector(selectCurrentCluster);
  const {
    stage,
    loading,
    message,
    isLogin,
    toggleLegality,
    legalityChecked,
    botLoginAction,
    currentUser
  } = useTelegramLogin();
  const { open } = useLegalityModal();

  if (currentUser) {
    return <Navigate to={ROUTES.MAIN} replace />;
  }

  const handleBotPress = () => {
    window.open(getBotUrl(), "_blank");
  };

  const checkBoxTitle = (
    <div className={styles.linksContainer}>
      <span className={styles.toggleLabel}>Согласие с </span>
      <button
        className={styles.link}
        onClick={() =>
          open(
            currentCluster?.id!,
            currentCluster?.name,
            LegalityScreens.PERSONAL_DATA_AGREEMENT,
          )
        }
      >
        соглашением об обработке персональных данных
      </button>
      <button
        className={styles.link}
        onClick={() =>
          open(
            currentCluster?.id!,
            currentCluster?.name,
            LegalityScreens.USER_AGREEMENT,
          )
        }
      >
        пользовательским соглашением
      </button>
      <button
        className={styles.link}
        onClick={() =>
          open(
            currentCluster?.id!,
            currentCluster?.name,
            LegalityScreens.PRIVACY_POLICY,
          )
        }
      >
        политикой в отношении обработки персональных данных
      </button>
    </div>
  );

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta/>
      
      {/* Мобильная стилизованная шапка */}
      <div
        className={styles.mobileHeader}
        style={{ backgroundColor: theme.primary }}
      >
        <BackButton />
        <h1 className={styles.mobileHeaderTitle}>
          {isLogin ? "Авторизация" : "Регистрация"}
        </h1>
      </div>

      {/* Десктопный ProductHeader */}
      <div className={styles.desktopHeader}>
        <ProductHeader
          title={isLogin ? "Авторизация" : "Регистрация"}
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
          // backgroundColor={theme.background}
        />
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loaderContainer}>
            <Loader />
            {message && (
              <p
                className={styles.messageText}
                style={{ color: theme.textSecondary }}
              >
                {message}
              </p>
            )}
          </div>
        ) : (
          <>
            <h1 className={styles.title} style={{ color: theme.text }}>
              {isLogin ? "Авторизация" : "Регистрация"}
            </h1>

            {stage === LoginStage.botLink ? (
              <p className={styles.subtitle}>
                Создание аккаунта и авторизация на базе Telegram, после нажатия
                кнопки, вы будете перенаправлены в наш бот и следуйте
                инструкциям. После авторизации вы будете перенаправлены в
                приложение, не удаляйте наш бот. Мы будем информировать вас
                через него о важных для вас событиях. Необходим аккаунт Telegram
              </p>
            ) : (
              <p className={message ? styles.messageText : styles.subtitle}>
                {message ||
                  "Для использования всех функций приложения необходимо авторизоваться через Telegram бота"}
              </p>
            )}

            {!isLogin && stage === LoginStage.botLoginProcess ? (
              <>
                <div className={styles.toggleContainer}>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={legalityChecked}
                      onChange={toggleLegality}
                    />
                    <span className={styles.slider} />
                  </label>
                  {checkBoxTitle}
                </div>

                <div className={styles.buttonContainer}>
                  <Button
                    title={
                      isOnline
                        ? "Зарегистрироваться"
                        : "Для регистрации нужен интернет"
                    }
                    onClick={botLoginAction}
                    variant="primary"
                    disabled={!legalityChecked || !isOnline}
                    fullWidth
                  />
                </div>
              </>
            ) : stage === LoginStage.botLink ||
              stage === LoginStage.botLoginError ? (
              <div className={styles.buttonContainer}>
                <Button
                  title={
                    isOnline
                      ? "Перейти в бот"
                      : "Для авторизации нужен интернет"
                  }
                  onClick={handleBotPress}
                  variant="primary"
                  icon={<FaTelegramPlane size={20} />}
                  iconPosition="left"
                  disabled={!isOnline}
                  fullWidth
                />
              </div>
            ) : null}
          </>
        )}

        {!loading && stage !== LoginStage.botLoginProcess && (
          <p className={styles.infoText} style={{ color: theme.textSecondary }}>
            После авторизации в боте вы сможете просматривать историю заказов,
            получать персональные предложения и многое другое
          </p>
        )}
      </div>
    </div>
  );
}
